import { when } from 'mobx';
import { inject } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { FRESHDESK, ShareSettingsConstants as ui } from '../../Config/UIConstants';
import { ExitableAlert } from '../../SharedComponents/ExitableAlert';
import { FullContainer } from '../../SharedComponents/FullContainer';
import { FullScreenModalContainer } from '../../SharedComponents/FullScreenModalContainer';
import { InformationIcon } from '../../SharedComponents/InformationIcon';
import { ModalActionButtonBar } from '../../SharedComponents/ModalActionButtonBar';
import { RemovalConfirmationPopup } from '../../SharedComponents/RemovalConfirmationPopup';
import { IStory } from '../../Stores/StoryStore';
import { Routes, RouteFormats } from '../../_controllers/RouteController';
import { ApplicationStore } from '../../_stores/ApplicationStore';
import { CheckMarkCircleIcon } from '../../images/images';
import { colors } from '../../styles/colors';
import { largeShadow } from '../../styles/styleObjects';
import { Url } from '../../util/Url';
import { parseIntWithAssert } from '../../util/string.utils';
import { NEW_FAMILY_ID, RETURN_URL } from '../FamiliesPage/AddFamilyPage';
import parseSearchString = Url.parseSearchString;

type PropsIntersection = RouteComponentProps<IRouteProps> & IProps;

interface IState {
    storyName?: string;
    original: {
        storyName?: string,
        selectedFamilyIndex: number
    };
    selectedFamilyIndex: number;
    warnShareStory: boolean;
    showExitWarning: boolean;
    familyNameShared: string;
}

interface IProps {
    store: ApplicationStore;
}

interface IRouteProps {
    storyId?: string
}

@inject('store')
export class ShareStoryModal extends React.Component<PropsIntersection, IState> {
    private readonly selectedFamilyRef: React.RefObject<HTMLDivElement>;

    public state: IState = {
        storyName: undefined,
        selectedFamilyIndex: -1,
        warnShareStory: false,
        original: { selectedFamilyIndex: -1 },
        showExitWarning: false,
        familyNameShared: '',
    };

    constructor(props: PropsIntersection, context: unknown) {
        super(props, context);

        this.selectedFamilyRef = React.createRef();
    }

    public componentDidMount(): void {
        when(
            () => this.props.store.storyStore && this.props.store.storyStore.storiesAreLoaded,
            () => {
                const parsedStoryId = parseIntWithAssert(this.props.match.params.storyId);
                if (!parsedStoryId) {
                    return;
                }

                let selectedFamilyIndex = -1;

                const parsedQueryString = parseSearchString(this.props.location.search);
                let newFamilyId = parseIntWithAssert(parsedQueryString[NEW_FAMILY_ID]);
                if (newFamilyId) {
                    selectedFamilyIndex = this.props.store.userFamilyStore.userFamilies
                        .findIndex(family => family.id === newFamilyId);
                }

                const story = this.props.store.storyStore.getStoryByID(parsedStoryId);
                this.setState({
                    storyName: story.title,
                    original: { storyName: story.title, selectedFamilyIndex: story.sharedWithFamilyID || -1 },
                    familyNameShared: story.sharedWithFamilyID
                        ? this.props.store.userFamilyStore.getFamilyByID(story.sharedWithFamilyID).name
                        : '',
                    selectedFamilyIndex,
                }, () => {
                    const elSelectedFamily = this.selectedFamilyRef.current;
                    if (elSelectedFamily) {
                        elSelectedFamily.scrollIntoView();
                    }
                });

            },
        );
    }

    private async saveStorySettings(story: IStory): Promise<void> {
        this.setState({ warnShareStory: false });
        const families = this.props.store.userFamilyStore.userFamilies;
        const familyID = families[this.state.selectedFamilyIndex].id;
        await this.props.store.familyStore.addFamilyToCollection(story.id, familyID);
        story.isPrivate = false;
        story.sharedWithFamilyID = familyID;
        this.props.history.push(Routes.ALBUMS);
    }

    private onClickSave(): void {
        if (this.state.selectedFamilyIndex !== -1) {
            this.setState({ warnShareStory: true });
        }
    }

    private onPressExit(): void {
        if (this.state.selectedFamilyIndex !== -1) {
            this.setState({ showExitWarning: true });
            return;
        }
        this.props.history.push(Routes.ALBUMS);
    }

    private onPressCreateNewFamily = (storyId: number): void => {
        this.props.history.push(Routes.ADD_FAMILY + '?' + RETURN_URL + '=' +
            encodeURIComponent(RouteFormats.shareStory(storyId)));
    }

    public render() {
        if (
            !this.props.store.userFamilyStore ||
            !this.props.store.userFamilyStore.familiesAreLoaded
        ) {
            return null;
        }

        const routeStoryId = this.props.match.params.storyId;
        if (routeStoryId === undefined) {
            return null;
        }
        const storyId = parseInt(routeStoryId);
        if (isNaN(storyId)) {
            return null;
        }

        const story = this.props.store.storyStore.getStoryByID(storyId);

        const families = this.props.store.userFamilyStore.userFamilies;

        return (
            <FullScreenModalContainer {...this.props} onPressExit={() => this.onPressExit()}>
                <FullContainer style={styles.container}>
                    <h3 style={styles.headerText}>{ui.HEADER}</h3>
                    <h4 style={styles.storyName}>{this.state.storyName}</h4>
                    {(() => {

                        if (this.state.familyNameShared === '') {
                            return <div style={styles.innerContainer}>
                                <p style={styles.shareWarning}>{ui.ONE_FAMILY_PER_SHARE}</p>
                                <p style={styles.shareWarning}>{ui.CANNOT_UNDO_SHARE}</p>
                                <FullContainer>
                                    <div style={styles.tab}>
                                        {ui.SHARE_TAB}
                                        <InformationIcon
                                            link={FRESHDESK.SHARE_A_STORY}
                                            style={styles.tabInfoIcon}/>
                                        <div style={styles.activeIndicator}/>
                                    </div>

                                    <div style={styles.rightTabContainer}>
                                        {families.map((family, index) => {
                                            const isSelectedFamily = this.state.selectedFamilyIndex === index;
                                            let selectedFamilyRef: React.RefObject<HTMLDivElement> | undefined;
                                            if (isSelectedFamily) {
                                                selectedFamilyRef = this.selectedFamilyRef;
                                            }

                                            return (
                                                <div
                                                    onClick={() => this.setState({selectedFamilyIndex: index})}
                                                    key={family.id}
                                                    style={styles.familyRowContainer}
                                                    ref={selectedFamilyRef}
                                                >
                                                    <p>{family.name}</p>
                                                    <div>
                                                        {isSelectedFamily ? (
                                                            <img alt="Check mark"
                                                                 src={CheckMarkCircleIcon}
                                                                 style={styles.familyCheckmark}
                                                            />
                                                        ) : (
                                                            <div style={styles.emptyCheckMark}/>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        <div
                                            style={styles.familyRowContainer}
                                            onClick={() => this.onPressCreateNewFamily(storyId)}
                                        >
                                            <p style={styles.createFamilyText}>{ui.CREATE_NEW_FAMILY}</p>
                                        </div>
                                    </div>
                                </FullContainer>
                            </div>;
                        } else {
                            return <p style={styles.sharedWith}>{ui.SHARED_WITH + this.state.familyNameShared}</p>;
                        }})()}

                </FullContainer>

                <ModalActionButtonBar
                    cancelOnClick={
                        () => this.onPressExit()
                    }
                    style={styles.actionBar}
                    cancelText={ui.CANCEL}
                    confirmOnClick={() => this.onClickSave()}
                    confirmContent={ui.SAVE}
                    confirmStyle={styles.confirmButton}
                    altPadding={25}
                    confirmDisabled={
                        this.state.original.selectedFamilyIndex === this.state.selectedFamilyIndex
                    }
                />

                {this.state.warnShareStory && (
                    <RemovalConfirmationPopup
                        headerText={ui.SHARE_WARNING}
                        subHeaderText={ui.CANNOT_BE_UNDONE}
                        removeText={ui.GO_BACK}
                        cancelText={ui.CONTINUE}
                        onClickCancel={() => this.saveStorySettings(story)}
                        onClickRemove={() => this.setState({ warnShareStory: false })}
                    />
                )}

                {this.state.showExitWarning && (
                    <RemovalConfirmationPopup
                        headerText={ui.ARE_YOU_SURE}
                        subHeaderText={ui.UNSAVED_CHANGES}
                        removeText={ui.REMAIN_HERE}
                        cancelText={ui.LEAVE_PAGE}
                        onClickRemove={() => this.setState({ showExitWarning: false })}
                        onClickCancel={() =>
                            this.props.history.push(Routes.ALBUMS)
                        }
                    />
                )}

                {story.photosWithComments.length === 0 &&
                    <ExitableAlert
                        headerText={ui.CANNOT_SHARE}
                        onConfirmClick={() => {
                            this.props.history.push(Routes.ALBUMS);
                        }}
                        confirmText={`Ok`}
                    />}
            }
            </FullScreenModalContainer>
        );
    }
}

const styles = {
    sharedWith: { fontSize: 25 },
    storyName: {
        fontSize: '1.8em',
        color: '#423F4E',
        fontWeight: 900,
        backgroundColor: '#fff',
        padding: '0.4em',
        minWidth: '12em',
        textAlign: 'center',
        marginBottom: '1em',
    } as React.CSSProperties,
    createFamilyText: { color: colors.ACTION_GREEN },
    actionBar: { justifyContent: 'flex-end' } as React.CSSProperties,
    confirmButton: { marginLeft: 20 },
    deleteWarning: {
        fontSize: 14,
        textAlign: 'center',
        width: '110%',
    },
    deleteButton: {
        backgroundColor: colors.CANCEL_TEXT,
    },
    heightBuffer: {
        height: 50,
    },
    deleteStoryContainer: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        marginTop: 20,
    },
    shareWarning: {
        width: '100%',
        textAlign: 'center',
        marginBottom: 15,
    } as React.CSSProperties,
    innerContainer: {
        width: 500,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    } as React.CSSProperties,
    container: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        alignItems: 'center',
    } as React.CSSProperties,
    headerText: {
        fontSize: 25,
        color: '#423F4E',
        fontWeight: 900,
        marginTop: 70,
        height: 50,
    } as React.CSSProperties,
    input: {
        width: 400,
        height: 75,
        backgroundColor: 'white',
        marginTop: 40,
        boxShadow: largeShadow,
    },
    familyRowContainer: {
        height: 45,
        cursor: 'pointer',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
        display: 'flex',
        paddingLeft: 15,
        paddingRight: 15,
        borderBottom: '1px solid rgb(245, 245, 245)',
    } as React.CSSProperties,
    familyCheckmark: {
        height: 20,
        width: 20,
        borderRadius: 20,
    },
    rightTabText: {
        color: 'black',
        paddingTop: 3,
        marginLeft: 8,
        textAlign: 'center',
        width: '100%',
    },
    rightTabContainer: {
        width: '100%',
        backgroundColor: 'white',
        alignItems: 'center',
        flexDirection: 'column',
        maxHeight: 285,
        overflowY: 'scroll',
    } as React.CSSProperties,
    lockText: {
        color: 'black',
        paddingTop: 3,
        marginLeft: 8,
    },
    lockImage: {
        marginLeft: 15,
        height: 15,
        width: 15,
        objectFit: 'contain',
    },
    leftTabContainer: {
        width: '100%',
        backgroundColor: 'white',
        height: 50,
        display: 'flex',
        alignItems: 'center',
    },
    tab: {
        display: 'flex',
        height: '55px',
        flex: 1,
        position: 'relative',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        textAlign: 'center',
        color: '#48A29B',
        fontWeight: 900
    } as React.CSSProperties,
    tabInfoIcon: {
        position: 'absolute',
        height: '2em',
        right: '-2.5em'
    } as React.CSSProperties,
    activeIndicator: {
        height: 4,
        width: '100%',
        backgroundColor: '#48A29B',
        position: 'absolute',
        bottom: 0
    } as React.CSSProperties,
    emptyCheckMark: {
        height: 20,
        width: 20,
        borderRadius: 20,
        border: '1px solid #00000022',
    },
};



// WEBPACK FOOTER //
// ./src/Pages/StoriesPage/ShareStoryModal.tsx